import { ResponseError } from "app/services/fetchService";

import { createMessageId } from "../../helpers/onboardingAgentHelpers";
import type {
    OnboardingAgentQuickAction,
    OnboardingAgentResponse,
    OnboardingAgentSuggestedPage,
} from "../../onboardingAgent.types";
import type {
    OnboardingAgentAssistantExploreNextItem,
    OnboardingAgentAssistantQuickAction,
} from "../../OnboardingAgentAssistantMessage/OnboardingAgentAssistantMessage.types";
import {
    CONVERSATION_MESSAGE_SEPARATOR,
    ONBOARDING_AGENT_QUOTA_EXCEEDED_MARKER,
    ONBOARDING_AGENT_QUOTA_EXCEEDED_STATUS,
    WEBSITE_CONTENT_SELECTED_TAB_FOLDERS,
    WEBSITE_CONTENT_UI_ROUTER_STATE,
    WEBSITE_PERFORMANCE_UI_ROUTER_STATE,
} from "../../onboardingAgentConstants";
import type { OnboardingAgentMessageVariables } from "../hooks/useOnboardingAgentMessageMutation";
import type { ChatAssistantMessage, ChatMessage } from "../OnboardingAgentPanel.types";

export const createOnboardingAgentRequest = (
    message: string,
    threadId: string | null,
): OnboardingAgentMessageVariables => ({
    requestId: createMessageId(),
    message,
    threadId,
});

const mapSuggestedPageToExploreNextItem = (
    suggestedPage: OnboardingAgentSuggestedPage,
): OnboardingAgentAssistantExploreNextItem => ({
    label: suggestedPage.title,
    uiRouterState: suggestedPage.uiRouterState,
    link: suggestedPage.link,
});

const mapQuickActionToAssistantQuickAction = (
    action: OnboardingAgentQuickAction,
): OnboardingAgentAssistantQuickAction => ({
    label: action.label,
    type: action.type,
    target: action.target,
});

export const mapResponseToAssistantMessage = (
    response: OnboardingAgentResponse,
): Omit<ChatAssistantMessage, "id" | "role"> => ({
    text: response.responseText,
    copyPlainText: response.responseText,
    threadId: response.threadId || undefined,
    exploreNextItems: response.suggestedPages.map(mapSuggestedPageToExploreNextItem),
    followUpQuestions: [...response.followUpQuestions],
    quickActions: response.quickActions.map(mapQuickActionToAssistantQuickAction),
    suggestedActions: [...response.suggestedActions],
});

export const isLimitReachedError = (error: Error): boolean => {
    if (!(error instanceof ResponseError)) {
        return false;
    }

    if (error.status !== ONBOARDING_AGENT_QUOTA_EXCEEDED_STATUS) {
        return false;
    }

    const errorName = error.name ?? "";

    return errorName.includes(ONBOARDING_AGENT_QUOTA_EXCEEDED_MARKER);
};

export const createConversationPrint = (messages: readonly ChatMessage[]): string =>
    messages.map((message) => `${message.role}: ${message.text}`).join(CONVERSATION_MESSAGE_SEPARATOR);

export const createQuickLinksTrackingPayload = (suggestedPages: readonly OnboardingAgentSuggestedPage[]): string =>
    suggestedPages
        .map((suggestedPage) => `${suggestedPage.title}: ${suggestedPage.uiRouterState || suggestedPage.link}`)
        .join(CONVERSATION_MESSAGE_SEPARATOR);

export type DirectLinkAssistantMessageCopy = {
    readonly message: string;
    readonly websitePerformanceLabel: string;
    readonly websiteContentLabel: string;
};

/** Client-only reply — no backend call; reuses Explore Next for the quick-link navigation. */
export const createDirectLinkAssistantMessage = (
    domain: string,
    copy: DirectLinkAssistantMessageCopy,
): Omit<ChatAssistantMessage, "id" | "role"> => ({
    text: copy.message,
    copyPlainText: copy.message,
    exploreNextItems: [
        {
            label: copy.websitePerformanceLabel,
            uiRouterState: WEBSITE_PERFORMANCE_UI_ROUTER_STATE,
            link: "",
            params: { key: domain },
        },
        {
            label: copy.websiteContentLabel,
            uiRouterState: WEBSITE_CONTENT_UI_ROUTER_STATE,
            link: "",
            params: { key: domain, selectedTab: WEBSITE_CONTENT_SELECTED_TAB_FOLDERS },
        },
    ],
});
